import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert, Zap, Lightbulb, RefreshCw, Play, Pause, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import messages from "@/api/messages";

const CATEGORIES = [
  {
    key: "safety",
    icon: ShieldAlert,
    // Light → soft amber-rose; dark → muted ember.
    bgLight: "linear-gradient(135deg, #FEF3C7 0%, #FED7AA 100%)",
    bgDark: "linear-gradient(135deg, rgba(247,197,109,0.18) 0%, rgba(247,118,142,0.22) 100%)",
    accent: "#F97316",
    accentText: "#9A3412",
  },
  {
    key: "productivity",
    icon: Zap,
    bgLight: "linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%)",
    bgDark: "linear-gradient(135deg, rgba(158,206,106,0.18) 0%, rgba(125,207,255,0.22) 100%)",
    accent: "#10B981",
    accentText: "#065F46",
  },
  {
    key: "info",
    icon: Lightbulb,
    bgLight: "linear-gradient(135deg, #DBEAFE 0%, #E0E7FF 100%)",
    bgDark: "linear-gradient(135deg, rgba(125,207,255,0.18) 0%, rgba(187,154,247,0.22) 100%)",
    accent: "#3B82F6",
    accentText: "#1E3A8A",
  },
];

const LS_AUTOROTATE_KEY = "muleria_ai_msg_autorotate";
const AUTOROTATE_MS = 20_000;

function detectIsDark() {
  // We follow the same heuristic as the rest of the app: html.dark class.
  if (typeof document === "undefined") return false;
  return document.documentElement.classList.contains("dark");
}

export default function AISafetyCard({ defaultCategory = "safety", testIdPrefix = "ai-msg" }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "es").startsWith("en") ? "en" : "es";

  const [category, setCategory] = useState(defaultCategory);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRotate, setAutoRotate] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(LS_AUTOROTATE_KEY) === "1";
  });
  const [isDark, setIsDark] = useState(detectIsDark);
  const timerRef = useRef(null);

  const cat = useMemo(
    () => CATEGORIES.find((c) => c.key === category) || CATEGORIES[0],
    [category]
  );

  const fetchMessage = useCallback(
    async (fresh = false) => {
      setLoading(true);
      try {
        const r = await messages.ai(category, lang, fresh);
        setData(r.data);
      } catch (err) {
        // Intentionally swallowed: the endpoint always returns 200 with a
        // fallback message, so a thrown error means network failure. Keep the
        // prior `data` snapshot so the card doesn't go blank.
        console.debug("AISafetyCard.fetchMessage failed:", err?.message);
      } finally {
        setLoading(false);
      }
    },
    [category, lang]
  );

  // Reload when category or lang changes.
  useEffect(() => {
    fetchMessage(false);
  }, [fetchMessage]);

  // Auto-rotate timer (independent of fetchMessage identity).
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoRotate) {
      timerRef.current = setInterval(() => {
        fetchMessage(true);
      }, AUTOROTATE_MS);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRotate, fetchMessage]);

  // Persist auto-rotate preference.
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LS_AUTOROTATE_KEY, autoRotate ? "1" : "0");
  }, [autoRotate]);

  // Watch for dark mode toggles so gradients stay readable.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const obs = new MutationObserver(() => setIsDark(detectIsDark()));
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);

  const Icon = cat.icon;
  const background = isDark ? cat.bgDark : cat.bgLight;

  return (
    <Card
      className="overflow-hidden border-0 shadow-sm"
      data-testid={`${testIdPrefix}-card`}
      data-category={category}
      style={{ background }}
    >
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div
              className="flex h-11 w-11 items-center justify-center rounded-full"
              style={{
                background: cat.accent,
                boxShadow: `0 4px 14px ${cat.accent}55`,
              }}
            >
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: isDark ? "#F5F5F5" : cat.accentText }}
              >
                {t(`aiMessages.title.${category}`)}
              </div>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                {data?.source === "llm"
                  ? t("aiMessages.sourceLlm")
                  : t("aiMessages.sourceFallback")}
              </div>
            </div>
          </div>
          {/* Category chips */}
          <div
            className="flex gap-1 rounded-full bg-background/70 p-0.5 backdrop-blur"
            data-testid={`${testIdPrefix}-category-chips`}
          >
            {CATEGORIES.map((c) => {
              const active = c.key === category;
              const CIcon = c.icon;
              return (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider transition-colors ${
                    active
                      ? "text-white shadow"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                  style={active ? { background: c.accent } : undefined}
                  data-testid={`${testIdPrefix}-chip-${c.key}`}
                  aria-pressed={active}
                >
                  <CIcon className="h-3 w-3" />
                  <span className="hidden sm:inline">
                    {t(`aiMessages.chip.${c.key}`)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-1">
        {data ? (
          <>
            <div
              className="text-lg font-semibold leading-snug"
              style={{ color: isDark ? "#FAFAFA" : cat.accentText }}
              data-testid={`${testIdPrefix}-title`}
            >
              {data.title}
            </div>
            <div
              className="text-sm leading-relaxed"
              style={{ color: isDark ? "rgba(245,245,245,0.92)" : "rgba(20,20,20,0.78)" }}
              data-testid={`${testIdPrefix}-body`}
            >
              {data.message}
            </div>
          </>
        ) : (
          <div
            className="h-16 animate-pulse rounded bg-background/50"
            data-testid={`${testIdPrefix}-loading`}
          />
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <Badge
            variant="outline"
            className="border-0 text-[10px] uppercase tracking-wider"
            style={{
              background: `${cat.accent}25`,
              color: isDark ? "#FAFAFA" : cat.accentText,
            }}
            data-testid={`${testIdPrefix}-chip-current`}
          >
            {data?.icon} {t(`aiMessages.chip.${category}`)}
          </Badge>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => fetchMessage(true)}
              disabled={loading}
              data-testid={`${testIdPrefix}-regenerate`}
              className="h-8 gap-1.5 bg-background/80 hover:bg-background"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
              />
              {t("aiMessages.regenerate")}
            </Button>
            <Button
              size="sm"
              variant={autoRotate ? "default" : "secondary"}
              onClick={() => setAutoRotate((v) => !v)}
              data-testid={`${testIdPrefix}-autorotate`}
              className="h-8 gap-1.5"
              style={
                autoRotate
                  ? { background: cat.accent, color: "white" }
                  : { background: "rgba(255,255,255,0.7)" }
              }
            >
              {autoRotate ? (
                <Pause className="h-3.5 w-3.5" />
              ) : (
                <Play className="h-3.5 w-3.5" />
              )}
              {autoRotate
                ? t("aiMessages.autoRotateOn")
                : t("aiMessages.autoRotateOff")}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
